import type { ReactElement } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  CONFIDENCE_HIGH_MIN,
  CONFIDENCE_MEDIUM_MIN,
  CONFIDENCE_SCALE_HINT,
  getEntropyConfidence,
  type SecretConfidence,
} from './AlertSecretDetails.helpers';

/**
 * Confidence → severity-badge variant. Reuses the severity color scale on
 * purpose so the visual language matches: High confidence is the alarming color
 * (like High severity), Low is the calm one.
 */
const CONFIDENCE_VARIANT: Record<SecretConfidence, 'low' | 'medium' | 'high'> =
  {
    low: 'low',
    medium: 'medium',
    high: 'high',
  };

const CONFIDENCE_LABEL: Record<SecretConfidence, string> = {
  low: 'Low confidence',
  medium: 'Medium confidence',
  high: 'High confidence',
};

/**
 * Visible tooltip body explaining the confidence scale and the built-in rules'
 * bands. Kept as a component (not a bare node) so it can be exported alongside
 * the badge without tripping the `react-refresh/only-export-components` rule.
 */
export function ConfidenceScaleExplanation(): ReactElement {
  return (
    <div className="space-y-1.5 leading-relaxed">
      <p>
        Confidence that this is a real secret, derived from the matched value’s
        randomness (Shannon entropy) reported by the built-in secret detection
        engine. Bands follow the built-in rules’ default thresholds, which top
        out at {CONFIDENCE_HIGH_MIN}.
      </p>
      <ul className="list-disc space-y-0.5 pl-4">
        <li>
          <span className="font-semibold">Low</span> (below{' '}
          {CONFIDENCE_MEDIUM_MIN}): below most built-in rule thresholds — often
          a placeholder or low-randomness token.
        </li>
        <li>
          <span className="font-semibold">Medium</span> ({CONFIDENCE_MEDIUM_MIN}{' '}
          to below {CONFIDENCE_HIGH_MIN}): within the stricter built-in rule
          thresholds — likely a real secret.
        </li>
        <li>
          <span className="font-semibold">High</span> ({CONFIDENCE_HIGH_MIN} and
          above): meets or exceeds every default built-in rule threshold — very
          likely a randomly generated credential.
        </li>
      </ul>
    </div>
  );
}

interface ConfidenceBadgeProps {
  /** The confidence level to render. */
  confidence: SecretConfidence;
  className?: string;
}

/**
 * A colored "<level> confidence" badge for a given confidence level (KIR-3765),
 * sized to sit next to the severity badge. Colors reuse the severity scale; a
 * hover/focus tooltip explains what the level means and how the bands map to
 * entropy. Used both by the alert badge (via {@link SecretConfidenceBadge}) and
 * the Secret Alert Confidence Threshold setting control, so the badge language
 * is identical in both places.
 */
export function ConfidenceBadge({
  confidence,
  className,
}: ConfidenceBadgeProps): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant={CONFIDENCE_VARIANT[confidence]}
          tabIndex={0}
          aria-label={`${CONFIDENCE_LABEL[confidence]}. ${CONFIDENCE_SCALE_HINT}`}
          className={cn('cursor-default', className)}
        >
          {CONFIDENCE_LABEL[confidence]}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <ConfidenceScaleExplanation />
      </TooltipContent>
    </Tooltip>
  );
}

ConfidenceBadge.displayName = 'ConfidenceBadge';

interface SecretConfidenceBadgeProps {
  /** Shannon entropy (bits/char) of the matched value. */
  entropy: number;
  className?: string;
}

/**
 * A colored "<level> confidence" badge for a secret alert (KIR-3765), sized to
 * sit next to the severity badge. Maps entropy → level and delegates to
 * {@link ConfidenceBadge}.
 */
export function SecretConfidenceBadge({
  entropy,
  className,
}: SecretConfidenceBadgeProps): ReactElement {
  return (
    <ConfidenceBadge
      confidence={getEntropyConfidence(entropy)}
      className={className}
    />
  );
}

SecretConfidenceBadge.displayName = 'SecretConfidenceBadge';
